import { useEffect, useState, useRef } from "react";
import { Cake, MessageSquareText, X, Gift, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  useBirthdaysThisWeek,
  buildBirthdayWaLink,
  birthdayDayLabel,
} from "@/hooks/useBirthdaysThisWeek";

/**
 * Birthday Bell — admin-topbar dropdown that surfaces birthdays in the next 7 days.
 * Also auto-opens a one-time-per-session modal on first login if there are birthdays today.
 */
export default function BirthdayBell() {
  const { user } = useAuth();
  const { rows, count, eligible, loading } = useBirthdaysThisWeek(user);
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const wrapRef = useRef(null);
  const shownThisSession = useRef(false);

  // Auto-open modal once per day per device (localStorage = sticky across logins / page reloads)
  useEffect(() => {
    if (!eligible || loading || shownThisSession.current) return;
    if (count > 0) {
      const today = new Date().toISOString().slice(0, 10);
      const key = `birthday_modal_dismissed_${today}`;
      if (!localStorage.getItem(key)) {
        setModalOpen(true);
        localStorage.setItem(key, "1");
        shownThisSession.current = true;
      } else {
        shownThisSession.current = true;
      }
    }
  }, [eligible, loading, count]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!eligible) return null;

  return (
    <>
      <div className="relative" ref={wrapRef} data-testid="birthday-bell-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="relative p-2 rounded-full text-slate-600 hover:bg-red-50 hover:text-red-700 transition-colors"
          data-testid="birthday-bell"
          title="Birthdays this week"
        >
          <Cake className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1" data-testid="birthday-bell-badge">
              {count}
            </span>
          )}
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50" data-testid="birthday-dropdown">
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4" />
                <div>
                  <div className="text-sm font-bold">Birthdays this week</div>
                  <div className="text-[10px] opacity-90">Next 7 days · {count} {count === 1 ? "person" : "people"}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {count === 0 && (
                <div className="p-6 text-center text-xs text-slate-400">
                  <Cake className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  No birthdays in the next 7 days
                </div>
              )}
              {rows.map(emp => (
                <BirthdayRow key={emp.id} emp={emp} compact />
              ))}
            </div>
          </div>
        )}
      </div>

      {modalOpen && <BirthdayModal rows={rows} count={count} onClose={() => setModalOpen(false)} />}
    </>
  );
}

function BirthdayRow({ emp, compact = false }) {
  const waLink = buildBirthdayWaLink(emp);
  const isToday = emp.days_until === 0;
  return (
    <div className={`px-4 py-2.5 border-b border-slate-100 hover:bg-red-50/30 ${isToday ? "bg-red-50/50" : ""}`} data-testid={`birthday-row-${emp.id}`}>
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${isToday ? "bg-red-600 text-white" : "bg-red-100 text-red-700"}`}>
          {emp.name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1">
            {emp.name}
            {isToday && <span className="text-[9px] bg-red-600 text-white px-1 py-0.5 rounded font-bold">TODAY</span>}
          </div>
          <div className="text-[10px] text-slate-500 truncate">
            {emp.punch_id} · {emp.department_name || "—"}
          </div>
          <div className="text-[10px] mt-0.5">
            <span className={isToday ? "text-red-700 font-bold" : "text-slate-600"}>
              {birthdayDayLabel(emp.days_until)}
            </span>
            <span className="text-slate-400"> · Turns {emp.age_turning}</span>
          </div>
        </div>
        {waLink ? (
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors flex-shrink-0"
            data-testid={`wish-${emp.id}`}
            title="Send WhatsApp wish"
          >
            <MessageSquareText className="h-3 w-3" />
            {!compact && "Wish"}
          </a>
        ) : (
          <span className="text-[9px] text-slate-400" title="No phone on file">no #</span>
        )}
      </div>
    </div>
  );
}

function BirthdayModal({ rows, count, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="birthday-modal" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-red-500 via-red-600 to-red-600 text-white px-6 py-5 relative">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white" data-testid="birthday-modal-close">
            <X className="h-5 w-5" />
          </button>
          <Gift className="h-10 w-10 mb-2" />
          <h2 className="text-xl font-bold" style={{ fontFamily: "Chivo" }}>
            🎉 {count} {count === 1 ? "Birthday" : "Birthdays"} this week!
          </h2>
          <p className="text-xs opacity-90 mt-1">Send a quick WhatsApp wish to make their day.</p>
        </div>
        <div className="overflow-y-auto flex-1">
          {rows.map(emp => (
            <BirthdayRow key={emp.id} emp={emp} />
          ))}
        </div>
        <div className="p-3 bg-slate-50 border-t border-slate-200 text-center">
          <button onClick={onClose} className="text-xs text-slate-600 hover:text-red-700 inline-flex items-center gap-1" data-testid="birthday-modal-dismiss">
            Maybe later <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
